/*
  Cross-sell latéral du drawer panier (desktop uniquement).

  1. Charge les produits de la collection choisie (sections/api-cross-sell-pool.liquid), mis en cache 10 min.
  2. Exclut : produits déjà au panier, indisponibles, cartes cadeaux, handles exclus (réglage).
  3. Priorité 1 : produits de la même capsule (custom.capsule) que les produits du panier.
  4. Priorité 2 : produits du même type de produit, tirés "un peu au hasard"
     (aléatoire stable pour un panier donné, pour éviter que le panneau change à chaque clic).
  5. Affiche les cartes avec le snippet upsell-product existant (sections/api-upsell-product.liquid).
*/
(function () {
  'use strict';

  if (customElements.get('cross-sell-panel')) return;

  const CACHE_TTL = 10 * 60 * 1000;
  const MAX_PAGES = 8; // 8 x 50 = 400 produits max analysés
  const DESKTOP_QUERY = '(min-width: 1024px)';

  // Caches partagés entre le panneau desktop et le bloc mobile (une seule requête réseau)
  const POOL_PROMISES = {};
  const CARD_CACHE = {};
  const CARD_PROMISES = {};

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Générateur pseudo-aléatoire à graine (mulberry32) + hash de chaîne
  function hash(str) {
    let h = 1779033703 ^ str.length;
    for (let i = 0; i < str.length; i++) {
      h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    return h >>> 0;
  }
  function rng(seed) {
    let a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      let t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function shuffle(list, seed) {
    const rand = rng(seed);
    const out = list.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  class CrossSellPanel extends HTMLElement {
    connectedCallback() {
      this.collectionHandle = this.dataset.collection;
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.excluded = (this.dataset.exclude || '')
        .split(',')
        .map((h) => h.trim().toLowerCase())
        .filter(Boolean);
      this.list = this.querySelector('[data-cross-sell-list]');
      this.mode = this.dataset.mode === 'inline' ? 'inline' : 'side';
      this.lastKey = null;
      this.timer = null;
      this.mq = window.matchMedia(DESKTOP_QUERY);

      this.schedule = this.schedule.bind(this);
      ['theme:cart-drawer:open', 'theme:product:added', 'theme:cart:change', 'theme:cart:update', 'theme:cart:refresh'].forEach(
        (evt) => document.addEventListener(evt, this.schedule)
      );
      this.mq.addEventListener('change', this.schedule);

      // Préchauffage : on charge la collection dès que le navigateur est disponible,
      // pour que le panneau soit prêt à l'ouverture du drawer.
      const warm = () => {
        if (this.isActive()) this.getPool().catch(() => {});
      };
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 3000 });
      else setTimeout(warm, 1500);

      // Premier calcul (panier déjà rempli au chargement de la page)
      this.schedule();
    }

    disconnectedCallback() {
      ['theme:cart-drawer:open', 'theme:product:added', 'theme:cart:change', 'theme:cart:update', 'theme:cart:refresh'].forEach(
        (evt) => document.removeEventListener(evt, this.schedule)
      );
      this.mq.removeEventListener('change', this.schedule);
      clearTimeout(this.timer);
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update().catch((e) => console.warn('[cross-sell]', e)), 60);
    }

    // Desktop (≥ 1024px) : panneau latéral. Sinon : bloc intégré au drawer.
    isActive() {
      return this.mode === 'side' ? this.mq.matches : !this.mq.matches;
    }

    async update() {
      if (!this.collectionHandle || !this.isActive()) {
        this.hidden = true;
        this.lastKey = null;
        if (this.list) this.list.innerHTML = '';
        return;
      }

      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (!cart.items || cart.items.length === 0) {
        this.hidden = true;
        this.lastKey = null;
        return;
      }

      const pool = await this.getPool();
      const picks = this.select(cart, pool);
      const key = picks.map((p) => p.handle).join('|');

      if (picks.length === 0) {
        this.hidden = true;
        this.lastKey = null;
        if (this.list) this.list.innerHTML = '';
        return;
      }

      if (key !== this.lastKey) {
        const cards = await Promise.all(picks.map((p) => this.getCard(p.handle)));
        // un autre update a pu démarrer entre-temps
        const html = cards.filter(Boolean).join('');
        if (this.list) this.list.innerHTML = html;
        this.lastKey = key;
      }
      this.hidden = !this.list || this.list.children.length === 0;
    }

    // ---- Pool (collection) ----
    async getPool() {
      const cacheKey = `cross-sell-pool:v2:${this.collectionHandle}`;
      try {
        const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.t < CACHE_TTL && cached.items.length && cached.items[0].title) return cached.items;
      } catch (e) {}

      if (POOL_PROMISES[cacheKey]) return POOL_PROMISES[cacheKey];

      POOL_PROMISES[cacheKey] = (async () => {
        const fetchPage = async (page) => {
          const url = `${root()}collections/${encodeURIComponent(this.collectionHandle)}?section_id=api-cross-sell-pool&page=${page}`;
          const html = await fetch(url).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const json = doc.querySelector('script[data-cross-sell-pool]');
          let items = [];
          try {
            items = json ? JSON.parse(json.textContent) : [];
          } catch (e) {}
          const pagesEl = doc.querySelector('[data-cross-sell-pages]');
          const pages = pagesEl ? parseInt(pagesEl.dataset.crossSellPages, 10) || 1 : 1;
          return { items, pages };
        };

        // page 1 pour connaître le nombre de pages, puis les autres EN PARALLÈLE
        const first = await fetchPage(1);
        const total = Math.min(first.pages, MAX_PAGES);
        const rest = [];
        for (let page = 2; page <= total; page++) rest.push(fetchPage(page));
        const others = await Promise.all(rest);
        const items = others.reduce((all, r) => all.concat(r.items), first.items);

        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), items }));
        } catch (e) {}
        return items;
      })();

      try {
        return await POOL_PROMISES[cacheKey];
      } finally {
        delete POOL_PROMISES[cacheKey];
      }
    }

    // ---- Sélection ----
    select(cart, pool) {
      const inCart = new Set(cart.items.map((i) => i.product_id));
      const byId = new Map(pool.map((p) => [p.id, p]));

      // capsules / types des produits du panier (le plus récemment ajouté d'abord)
      const capsules = [];
      const types = [];
      cart.items.forEach((item) => {
        const p = byId.get(item.product_id);
        const cap = p && p.capsule ? p.capsule : '';
        const type = ((p && p.type) || item.product_type || '').trim().toLowerCase();
        if (cap && !capsules.includes(cap)) capsules.push(cap);
        if (type && !types.includes(type)) types.push(type);
      });

      const candidates = pool.filter(
        (p) =>
          p.available &&
          !p.gift &&
          !inCart.has(p.id) &&
          !this.excluded.includes(p.handle.toLowerCase())
      );

      // Un seul produit par "modèle" : "Collier Féeria – rose gold" et "Collier Féeria – doré"
      // = même modèle ("collier féeria") → une seule couleur proposée.
      const modelOf = (p) => (p.title || p.handle).split(/\s[–—-]\s/)[0].trim().toLowerCase();

      const picks = [];
      const taken = new Set();
      const takenModels = new Set();
      const add = (p) => {
        const model = modelOf(p);
        if (picks.length < this.limit && !taken.has(p.id) && !takenModels.has(model)) {
          picks.push(p);
          taken.add(p.id);
          takenModels.add(model);
        }
      };

      // 1) même capsule — ordre de la collection, capsule du dernier produit ajouté d'abord
      capsules.forEach((cap) => candidates.filter((p) => p.capsule === cap).forEach(add));

      // 2) même type de produit — aléatoire stable pour ce panier
      if (picks.length < this.limit && types.length) {
        const seed = hash(cart.token + '|' + cart.items.map((i) => i.product_id).sort().join(','));
        const sameType = candidates.filter((p) => !taken.has(p.id) && p.type && types.includes(p.type));
        shuffle(sameType, seed).forEach(add);
      }

      return picks;
    }

    // ---- Carte produit (snippet upsell-product existant) ----
    async getCard(handle) {
      if (CARD_CACHE[handle]) return CARD_CACHE[handle];
      if (CARD_PROMISES[handle]) return CARD_PROMISES[handle];
      const key = `cross-sell-card:${handle}`;
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (cached && Date.now() - cached.t < CACHE_TTL) {
          CARD_CACHE[handle] = cached.html;
          return cached.html;
        }
      } catch (e) {}

      CARD_PROMISES[handle] = (async () => {
        try {
          const html = await fetch(`${root()}products/${encodeURIComponent(handle)}?section_id=api-upsell-product`).then((r) => r.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const content = doc.querySelector('[data-api-content]');
          if (!content) return '';
          CARD_CACHE[handle] = content.innerHTML;
          try {
            sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), html: CARD_CACHE[handle] }));
          } catch (e) {}
          return CARD_CACHE[handle];
        } catch (e) {
          return '';
        } finally {
          delete CARD_PROMISES[handle];
        }
      })();
      return CARD_PROMISES[handle];
    }
  }

  customElements.define('cross-sell-panel', CrossSellPanel);
})();